import { env } from 'process'
import { create } from 'zustand'
import { persist, devtools } from 'zustand/middleware'
import { useMemo } from 'react'

export type BackgroundJobStatus =
  | 'NOT_FOUND'
  | 'IN_PROGRESS'
  | 'SUCCESS'
  | 'FAILED'

export type TrashActionType = 'DELETE_PERMANENTLY' | 'RESTORE' | 'MOVE_TO_TRASH'

export type FileTransferType = 'COPY' | 'MOVE'

export type BackgroundJobType =
  | TrashActionType
  | FileTransferType
  | 'DELETE_DEVICE'

export type BackgroundJob = {
  id: string
  actionId?: string | number // actionId is the id of the action that triggered a group of background jobs. If set, identical actionIds will be grouped and visualized together automatically.
  type: BackgroundJobType
  status: BackgroundJobStatus
}

export type BackgroundJobState = {
  backgroundJobs: Record<string, BackgroundJob>
  addBackgroundJob: (
    type: BackgroundJobType,
    id: string,
    actionId?: string | number,
  ) => void
  updateBackgroundJob: (id: string, status: BackgroundJobStatus) => void
  removeBackgroundJobs: (ids: string[]) => void
  removeBackgroundJobsByActionId: (actionId: string | number) => void
  completedJobsCount: number
}

export const useBackgroundJobStore = create<BackgroundJobState>()(
  devtools(
    persist(
      (set, get) => ({
        backgroundJobs: {},
        completedJobsCount: 0,
        addBackgroundJob: (type, id, actionId) => {
          set(state => ({
            backgroundJobs: {
              ...state.backgroundJobs,
              [id]: {
                id,
                type,
                status: 'IN_PROGRESS',
                actionId,
              },
            },
          }))
        },
        updateBackgroundJob: (id, status) => {
          const backgroundJob = get().backgroundJobs[id]
          if (!backgroundJob || backgroundJob.status === status) {
            return
          }
          set(state => ({
            completedJobsCount:
              status !== 'IN_PROGRESS'
                ? state.completedJobsCount + 1
                : state.completedJobsCount,
            backgroundJobs: {
              ...state.backgroundJobs,
              [id]: { ...state.backgroundJobs[id], status },
            },
          }))
        },
        removeBackgroundJobs: ids => {
          set(state => {
            const remainingJobs = { ...state.backgroundJobs }
            ids.forEach(id => {
              delete remainingJobs[id]
            })
            return {
              backgroundJobs: remainingJobs,
            }
          })
        },
        removeBackgroundJobsByActionId: actionId => {
          set(state => {
            const remainingJobs = { ...state.backgroundJobs }
            Object.keys(remainingJobs).forEach(id => {
              if (remainingJobs[id].actionId === actionId) {
                delete remainingJobs[id]
              }
            })
            return {
              backgroundJobs: remainingJobs,
            }
          })
        },
      }),
      { name: 'background-job-store' },
    ),
    { name: 'BackgroundJobStore', enabled: !env.isProduction },
  ),
)

export const addBackgroundJob = (
  type: BackgroundJobType,
  id: string,
  actionId?: string | number,
) => {
  return useBackgroundJobStore.getState().addBackgroundJob(type, id, actionId)
}

export const updateBackgroundJob = (
  id: string,
  status: BackgroundJobStatus,
) => {
  return useBackgroundJobStore.getState().updateBackgroundJob(id, status)
}

export const getOngoingBackgroundJobs = (type: BackgroundJobType) => {
  return Object.values(useBackgroundJobStore.getState().backgroundJobs).filter(
    job => job.type === type && job.status === 'IN_PROGRESS',
  )
}

export const useHasOngoingBackgroundJobs = (type?: BackgroundJobType) =>
  useBackgroundJobStore(state =>
    Object.values(state.backgroundJobs).some(
      value =>
        // return all types if no type is provided
        value.status === 'IN_PROGRESS' && (type ? value.type === type : true),
    ),
  )

export const removeBackgroundJobsByType = (type: BackgroundJobType) => {
  const { backgroundJobs, removeBackgroundJobs } =
    useBackgroundJobStore.getState()
  const idsToRemove = Object.keys(backgroundJobs).filter(
    id => backgroundJobs[id].type === type,
  )
  removeBackgroundJobs(idsToRemove)
}

export const removeBackgroundJobsByActionId = (actionId: string | number) => {
  return useBackgroundJobStore
    .getState()
    .removeBackgroundJobsByActionId(actionId)
}

export const useBackgroundJobActions = (): {
  actionId: string
  jobType: BackgroundJobType
  status: Exclude<BackgroundJobStatus, 'NOT_FOUND'>
}[] => {
  const backgroundJobs = useBackgroundJobStore(state => state.backgroundJobs)

  // Memoize the processed result to maintain referential equality (otherwise the consumer will re-render in an infinite loop)
  return useMemo(() => {
    const jobs = Object.values(backgroundJobs)

    if (jobs.length === 0) {
      return []
    }

    const jobsByActionId = jobs.reduce<Record<string, BackgroundJob[]>>(
      (acc, job) => {
        if (!job.actionId) {
          return acc
        }
        if (!acc[job.actionId]) {
          acc[job.actionId] = []
        }
        acc[job.actionId].push(job)
        return acc
      },
      {},
    )

    return Object.entries(jobsByActionId).map(([actionId, jobs]) => {
      const jobType = jobs[0].type
      const hasInProgress = jobs.some(job => job.status === 'IN_PROGRESS')

      if (hasInProgress) {
        return { actionId, jobType, status: 'IN_PROGRESS' }
      }

      const hasFailures = jobs.some(job => job.status === 'FAILED')
      const status = hasFailures ? 'FAILED' : 'SUCCESS'

      return { actionId, jobType, status }
    })
  }, [backgroundJobs])
}
